import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useVendorAuth } from "src/components/reusable/hooks/useVendorAuth";
import { useGiftFlowStore } from "./useGiftFlowStore";
import { useApiRoutes } from "src/components/reusable/hooks/useApiRoutes";
import { useGenericErrorModal } from "src/components/reusable/hooks/useGenericErrorModal";
import { constants } from "src/shared/constants";
import { useGlobalStore } from "src/components/reusable/hooks/useGlobalStore";
import { throttleTime } from "src/utils/utils";
import { useRedeemSuccessModal } from "./redeemGift/useRedeemSuccessModal";
import { routes } from "src/shared/routes";
import { findApiErrorMessage } from "src/components/reusable/utils/utils";
import { TGiftCodeDetails } from "./types";

export function useGiftFlow() {
  const { user } = useGlobalStore();
  const store = useGiftFlowStore();
  const router = useRouterExt();
  const { loginWithVendor } = useVendorAuth();
  const { validateRedemptionCodeApi, redeemPassGiftApi } = useApiRoutes();
  const errorModal = useGenericErrorModal();
  const redeemSuccessModal = useRedeemSuccessModal();

  const fireErrorModal = (err: any) => {
    const errorMessage =
      typeof err == "string" ? err : findApiErrorMessage(err);
    const onClose = async () => {
      errorModal.set({ isOpen: false });
      await router.pushSchema(routes.home);
      throttleTime(400, errorModal.reset);
    };
    errorModal.set({
      isOpen: true,
      hideTitleIcon: true,
      title: "Error",
      description: errorMessage,
      isConfirmButtonHidden: true,
      onClickClose: onClose,
      onClickCloseIcon: onClose,
    });
  };

  const login = async () => {
    try {
      store.setGiftFlow({ isAuthenticating: true });
      const redemptionCode = router?.query?.redemptionCode as string;
      const state = {
        teamSchema: router.query.teamSchema as string,
        authOrigin: constants.authOrigins.redeemGift,
        redirectRoute: {
          pathname: routes.redeem_gift,
          query: { redemptionCode, triggerRedeem: "1" },
        },
      };
      return await loginWithVendor(state);
    } catch (err) {
      fireErrorModal(err);
    } finally {
      store.setGiftFlow({ isAuthenticating: false });
    }
  };

  const verifyGiftCode = async (redemptionCode: string) => {
    let codeDetails: TGiftCodeDetails | null = null;
    try {
      store.setGiftFlow({ isVerifying: true });
      codeDetails = await validateRedemptionCodeApi.post({ redemptionCode });
      store.setGiftFlow({ giftCodeDetails: codeDetails });
    } catch (err) {
      fireErrorModal(err);
    } finally {
      store.setGiftFlow({ isVerifying: false });
      return codeDetails;
    }
  };

  const onLoadRedeemPage = async () => {
    const redemptionCode = router?.query?.redemptionCode as string;
    await verifyGiftCode(redemptionCode);

    const triggerRedeem = Boolean(router?.query?.triggerRedeem);
    if (triggerRedeem) await handleRedeem();
  };

  const onLoginApiSuccess = async (ev: any) => {
    const { state, loginRes } = ev?.detail;
    const isRedeemAuth = state?.authOrigin === constants.authOrigins.redeemGift;
    const isRegisterRes = Boolean(loginRes?.register);
    if (!isRedeemAuth || !isRegisterRes) return;

    const redemptionCode = state?.redirectRoute?.query?.redemptionCode;
    await verifyGiftCode(redemptionCode);
  };

  const handleRedeem = async () => {
    try {
      store.setGiftFlow({ isRedeeming: true });
      const redemptionCode = router?.query?.redemptionCode as string;
      await redeemPassGiftApi.post({ redemptionCode });
      redeemSuccessModal.open();
    } catch (err) {
      fireErrorModal(err);
    } finally {
      store.setGiftFlow({ isRedeeming: false });
    }
  };

  const onClickRedeem = async () => {
    if (!user) return login();
    else await handleRedeem();
  };

  return {
    ...store,
    verifyGiftCode,
    handleRedeem,
    onLoadRedeemPage,
    onLoginApiSuccess,
    onClickRedeem,
    redeemSuccessModal,
  };
}
